/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useState } from "react";
import { useTranslation } from "react-i18next"; // Adjust import based on your i18n library
import { postAutoclaveServices } from "../../../../../service/postAutoclaveServices";
import {
	ContainerForSterialization,
	PostSterilizationPayload,
	SterilizationParameters,
} from "../emptySterilization.types";

// Helper Functions
const formatTime = (datetimeStr: string) => {
	return datetimeStr.replace("T", " ") + ":00";
};

const buildSterilizationPayload = (
	params: SterilizationParameters,
	rawContainers: ContainerForSterialization[],
	mediaBatchId: string,
): PostSterilizationPayload => {
	return {
		...params,
		media_batch_id: mediaBatchId,
		containers: rawContainers.map((c) => ({
			container_id: Number(c.container_id),
		})),
		cycle_start_datetime: formatTime(params.cycle_start_datetime),
		cycle_end_datetime: formatTime(params.cycle_end_datetime),
		autoclave_id: Number(params.autoclave_id),
		temperature_celsius: Number(params.temperature_celsius),
		pressure_bar: Number(params.pressure_bar),
		duration_min: Number(params.duration_min),
	};
};

interface Props {
	eligiblePlates: number;
	toggleConfirmationModal: () => void;
	sterilizationParameters: SterilizationParameters;
	containers: ContainerForSterialization[];
	showToast: (message: string, toastType: "success" | "error") => void;
	handleSuccess: () => void;
	mediaBatchId: string;
}

// Main component
const ESConfirmationModal = ({
	eligiblePlates,
	toggleConfirmationModal,
	sterilizationParameters,
	containers,
	showToast,
	handleSuccess,
	mediaBatchId
}: Props) => {
	const { t } = useTranslation();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleConfirm = async () => {
		setIsSubmitting(true);
		try {
			const payload = buildSterilizationPayload(
				sterilizationParameters,
				containers,
				mediaBatchId
			);
			console.log(payload);
			const response =
				await postAutoclaveServices.postContainerSterilization(payload);
			handleSuccess();
		} catch (error: any) {
			showToast(
				error?.response?.data?.message ||
					t("postAutoclaveProcess.emptySterilization.unexpectedError"),
				"error",
			);
		} finally {
			setIsSubmitting(false);
			toggleConfirmationModal();
		}
	};

	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 animate-in fade-in duration-300">
			<div className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-300">
				<div className="p-12 text-center">
					<div className="w-24 h-24 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-8 border border-orange-100">
						<span className="material-symbols-outlined text-orange-500 text-5xl">
							inventory_2
						</span>
					</div>
					<h3 className="text-2xl font-black text-[#1E293B] mb-4 tracking-tight">
						{t("postAutoclaveProcess.emptySterilization.confirmSterilization")}
					</h3>
					<div className="bg-slate-50 rounded-3xl p-8 mb-8 space-y-6 text-left">
						<p className="text-sm font-bold text-slate-600 leading-relaxed">
							<span className="text-orange-600 font-black">
								{t(
									"postAutoclaveProcess.emptySterilization.platesCount",
									{ count: eligiblePlates },
								)}
							</span>{" "}
							{t(
								"postAutoclaveProcess.emptySterilization.completionText",
							)}
						</p>
						<p className="text-xs text-slate-400 font-medium leading-relaxed">
							{t("postAutoclaveProcess.emptySterilization.description")}
						</p>
						<div className="h-px bg-slate-200 w-full"></div>
						<p className="text-sm font-black text-[#1E293B] text-center">
							{t(
								"postAutoclaveProcess.emptySterilization.confirmationPrompt",
							)}
						</p>
					</div>

					<div className="flex gap-4">
						<button
							onClick={() => toggleConfirmationModal()}
							disabled={isSubmitting}
							className="flex-1 px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{t("postAutoclaveProcess.emptySterilization.cancelBtn")}
						</button>
						<button
							onClick={handleConfirm}
							disabled={isSubmitting}
							className="flex-1 px-8 py-4 bg-[#2D3E50] text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-slate-200 hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
						>
							{isSubmitting ? (
								<>
									<span className="material-symbols-outlined text-lg animate-spin">
										progress_activity
									</span>
									{t(
										"postAutoclaveProcess.emptySterilization.updatingBtn",
									)}
								</>
							) : (
								t(
									"postAutoclaveProcess.emptySterilization.confirmUpdateBtn",
								)
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ESConfirmationModal;
